if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      getAnimationSourceImage() {
        const selectors = [
          '.product-media-container img',
          '.product__media img',
          '.product__media-item img',
          '.product__modal-opener img',
          '.card__media img',
          '.card__inner img',
        ];

        const roots = [
          this.closest('quick-add-modal'),
          this.closest('product-info'),
          this.closest('.product'),
          this.closest('.card-wrapper'),
          this.closest('section'),
          document,
        ].filter(Boolean);

        const seen = new Set();

        for (const root of roots) {
          for (const selector of selectors) {
            const matches = root.querySelectorAll(selector);
            for (const match of matches) {
              if (seen.has(match)) continue;
              seen.add(match);

              const rect = match.getBoundingClientRect();
              const style = window.getComputedStyle(match);

              if (
                rect.width > 48 &&
                rect.height > 48 &&
                style.display !== 'none' &&
                style.visibility !== 'hidden' &&
                match.currentSrc
              ) {
                return match;
              }
            }
          }
        }

        return null;
      }

      animateAddToCart() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const sourceImage = this.getAnimationSourceImage();
        const cartIcon = document.getElementById('cart-icon-bubble');
        const target =
          cartIcon?.querySelector('.cart-count-bubble') || cartIcon?.querySelector('.icon') || cartIcon;

        if (!sourceImage || !target) return;

        const sourceRect = sourceImage.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();

        if (sourceRect.width < 24 || sourceRect.height < 24 || targetRect.width < 16 || targetRect.height < 16) return;

        const flyImage = document.createElement('img');
        const startSize = Math.max(64, Math.min(92, Math.min(sourceRect.width, sourceRect.height)));
        const startLeft = sourceRect.left + sourceRect.width / 2 - startSize / 2;
        const startTop = sourceRect.top + sourceRect.height / 2 - startSize / 2;
        const deltaX = targetRect.left + targetRect.width / 2 - (startLeft + startSize / 2);
        const deltaY = targetRect.top + targetRect.height / 2 - (startTop + startSize / 2);

        flyImage.src = sourceImage.currentSrc || sourceImage.src;
        flyImage.alt = '';
        flyImage.setAttribute('aria-hidden', 'true');
        flyImage.style.position = 'fixed';
        flyImage.style.left = `${startLeft}px`;
        flyImage.style.top = `${startTop}px`;
        flyImage.style.width = `${startSize}px`;
        flyImage.style.height = `${startSize}px`;
        flyImage.style.objectFit = 'cover';
        flyImage.style.borderRadius = '1.8rem';
        flyImage.style.boxShadow = '0 18px 45px rgba(10, 20, 36, 0.24)';
        flyImage.style.pointerEvents = 'none';
        flyImage.style.zIndex = '2147483647';
        flyImage.style.opacity = '0.96';
        flyImage.style.transform = 'translate3d(0, 0, 0) scale(1)';
        flyImage.style.transition =
          'transform 460ms cubic-bezier(0.22, 0.8, 0.24, 1), opacity 460ms cubic-bezier(0.22, 0.8, 0.24, 1), border-radius 460ms cubic-bezier(0.22, 0.8, 0.24, 1)';

        document.body.appendChild(flyImage);

        requestAnimationFrame(() => {
          flyImage.style.transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(0.18)`;
          flyImage.style.opacity = '0.06';
          flyImage.style.borderRadius = '999px';
        });

        window.setTimeout(() => cartIcon.classList.add('is-cart-bumped'), 180);
        window.setTimeout(() => cartIcon.classList.remove('is-cart-bumped'), 640);
        window.setTimeout(() => flyImage.remove(), 620);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            this.animateAddToCart();
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  }, 120);
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              setTimeout(() => {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(response);
                });
              }, 120);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
